import React, { useState, useEffect } from "react";
import {
    TableRow,
    TableCell,
    Collapse,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Grid,
    useTheme
} from "@mui/material";
import { Save as SaveIcon, Cancel as CancelIcon } from "@mui/icons-material";
import { UserWithRole, UpdateUserDto } from "../../../../../models/User";
import { UsersService } from "../../../../../services/supabase/UsersService";
import { roleManagementStore } from "../../../../../app/stores/RoleManagement";
import { RoleManagementService } from "../../../../../services/supabase/RoleManagementService";
import { BasicButton } from "../../../../../components/BasicButton";
import toastService from "../../../../../services/toast/ToastService";
import { PermissionWrapper } from "../../../../../components/PermissionWrapper";

interface UserEditorProps {
    user: UserWithRole;
    open: boolean;
    isEditing: boolean;
    setIsEditing: (isEditing: boolean) => void;
}

export const UserEditor: React.FC<UserEditorProps> = ({ 
    user, 
    open, 
    isEditing, 
    setIsEditing 
}) => {
    const theme = useTheme();
    const { roles } = roleManagementStore();
    const { updateUserRole } = UsersService();
    
    const [formData, setFormData] = useState<UpdateUserDto>({});
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        setFormData({
            firstName: user.firstName,
            lastName: user.lastName,
            mobileNumber: user.mobileNumber,
            roleId: user.roleId,
            role: user.roleName
        });
    }, [user]);

    // Load roles when component opens
    useEffect(() => {
        if (open && roles.length === 0) {
            RoleManagementService().getAllRoles();
        }
    }, [open, roles.length]);

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleSave = async () => {
        setIsLoading(true);
        const updatedUser = await updateUserRole(user.id, formData);
        setIsLoading(false);

        if (updatedUser) {
            toastService.showSuccess("User updated successfully");
            setIsEditing(false);
        }
    };

    const handleCancel = () => {
        setFormData({
            firstName: user.firstName,
            lastName: user.lastName,
            mobileNumber: user.mobileNumber,
            roleId: user.roleId,
            role: user.roleName
        });
        setIsEditing(false);
    };

    return (
        <TableRow>
            <TableCell
                style={{
                    paddingBottom: 0,
                    paddingTop: 0,
                    backgroundColor: theme.palette.mode === "dark"
                        ? theme.palette.background.default
                        : theme.palette.grey[100]
                }}
                colSpan={6}
            >
                <Collapse in={open} timeout="auto" unmountOnExit>
                    <Box sx={{ marginX: 2, marginY: 4, padding: 2 }}>
                        <Grid container spacing={2} justifyContent="space-between" alignItems="center" mb={3}>
                            <Grid item>
                                <PermissionWrapper screen="users" permission="update">
                                {isEditing ? (
                                    <Box>
                                        <BasicButton
                                            title="Save"
                                            variant="contained"
                                            onClick={handleSave}
                                            startIcon={<SaveIcon />}
                                            sx={{ mr: 1, maxHeight: 36, fontSize: 12 }}
                                            style={{ backgroundColor: theme.palette.success.main }}
                                            isLoading={isLoading}
                                        />
                                        <BasicButton
                                            title="Cancel"
                                            variant="outlined"
                                            onClick={handleCancel}
                                            startIcon={<CancelIcon />}
                                            sx={{ maxHeight: 36, fontSize: 12 }}
                                        />
                                    </Box>
                                ) : (
                                        <BasicButton
                                            title="Edit User"
                                            variant="outlined"
                                            onClick={handleEdit}
                                            sx={{
                                                maxHeight: 36,
                                                fontSize: 12,
                                                backgroundColor: theme.palette.background.paper
                                            }}
                                        />
                                    )}
                                </PermissionWrapper>
                            </Grid>
                        </Grid>

                        <Box sx={{ opacity: isEditing ? 1 : 0.7 }}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Typography 
                                        variant="h6" 
                                        fontWeight="bold" 
                                        mb={2}
                                        sx={{ fontFamily: "primaryFont", letterSpacing: 0.5 }}
                                    >
                                        Personal Information
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <TextField
                                                label="First Name"
                                                fullWidth
                                                size="small"
                                                value={formData.firstName || ""}
                                                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                                                disabled={!isEditing}
                                                sx={{ backgroundColor: theme.palette.background.paper }}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                label="Last Name"
                                                fullWidth
                                                size="small"
                                                value={formData.lastName || ""}
                                                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                                                disabled={!isEditing}
                                                sx={{ backgroundColor: theme.palette.background.paper }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                label="Email"
                                                fullWidth
                                                size="small"
                                                value={user.email}
                                                disabled
                                                helperText="Email cannot be changed"
                                                sx={{ backgroundColor: theme.palette.background.paper }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                label="Mobile Number"
                                                fullWidth
                                                size="small"
                                                value={formData.mobileNumber || ""}
                                                onChange={(e) => setFormData({ ...formData, mobileNumber: e.target.value })}
                                                disabled={!isEditing}
                                                sx={{ backgroundColor: theme.palette.background.paper }}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography 
                                        variant="h6" 
                                        fontWeight="bold" 
                                        mb={2}
                                        sx={{ fontFamily: "primaryFont", letterSpacing: 0.5 }}
                                    >
                                        Role Assignment
                                    </Typography>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Role</InputLabel>
                                        <Select
                                            value={formData.roleId || ""}
                                            label="Role"
                                            onChange={(e) => {
                                                const selectedRole = roles.find(role => role.roleId === e.target.value);
                                                setFormData({
                                                    ...formData,
                                                    roleId: e.target.value,
                                                    role: selectedRole?.roleName || ""
                                                });
                                            }}
                                            disabled={!isEditing || roles.length === 0}
                                            sx={{ backgroundColor: theme.palette.background.paper }}
                                        >
                                            {roles.length === 0 ? (
                                                <MenuItem disabled>
                                                    <Typography color="text.secondary">Loading roles...</Typography>
                                                </MenuItem>
                                            ) : (
                                                roles.map((role) => (
                                                    <MenuItem key={role.roleId} value={role.roleId}>
                                                        <Box display="flex" justifyContent="space-between" width="100%">
                                                            <Typography>{role.roleName}</Typography>
                                                            {role.isSystem && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    (System)
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </MenuItem>
                                                ))
                                            )}
                                        </Select>
                                    </FormControl>
                                    <Box mt={2} p={2} sx={{ backgroundColor: theme.palette.action.hover, borderRadius: 2 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            <strong>Current Role:</strong> {user.roleName}
                                            {user.isSystemRole && " (System Role)"}
                                        </Typography>
                                        {user.roleDescription && (
                                            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                                                {user.roleDescription}
                                            </Typography>
                                        )}
                                        {formData.roleId !== user.roleId && (
                                            <Typography variant="caption" color="warning.main" display="block" mt={1}>
                                                ⚠️ Role will be changed to "{formData.role}" upon saving
                                            </Typography>
                                        )}
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>
                </Collapse>
            </TableCell>
        </TableRow>
    );
};
